/**
 * A recorded session (a trajectory, docs/NEW-UI/protocollo.md §8) read as the same events the live
 * kiosk receives. The replay feeds them to the same stores, so the interface looks exactly as it did.
 *
 * What the trajectory does not carry: the user's own bubble is not an SSE event there, so it is
 * rebuilt from `turn_start` (the same text `user_message_ack` would have carried).
 */
import { parseServerEvent, type InputSource, type ServerEvent } from '../core/wire';

export interface RunInfo {
  domain?: string;
  operator?: string;
  model?: string;
  surface?: string;
  code?: string;
  [key: string]: unknown;
}

export interface TurnSummary {
  index: number;
  turnId: string;
  source: InputSource;
  /** The words, or the touch utterance. */
  text: string;
  /** Present for a touch: the button and its data. */
  button?: string;
  data?: unknown;
  stateBefore: string;
  stateAfter?: string;
  action?: string;
  nluPath?: string;
  confidence?: number;
  totalMs?: number;
  blocked?: boolean;
  /** Position of this turn's first event in `steps`. */
  firstStep: number;
}

export interface RecordedStep {
  /** Seconds since the session started. */
  at: number;
  turnIndex: number | null;
  event: ServerEvent;
}

export interface Recording {
  sessionId: string;
  language: string;
  run: RunInfo | null;
  /** What the screens showed when it was recorded (the spec's `ui`, at the head); null in older recordings. */
  ui: unknown;
  turns: TurnSummary[];
  steps: RecordedStep[];
  /** Lines that could not be read, with why. */
  skipped: string[];
}

interface RawLine {
  ts_rel?: number;
  type?: string;
  data?: Record<string, unknown>;
}

export function parseRecording(ndjson: string): Recording {
  const rec: Recording = { sessionId: '', language: 'it', run: null, ui: null, turns: [], steps: [], skipped: [] };
  let current: TurnSummary | null = null;
  ndjson.split('\n').forEach((line, n) => {
    if (!line.trim()) return;
    let raw: RawLine;
    try {
      raw = JSON.parse(line) as RawLine;
    } catch {
      rec.skipped.push(`line ${n + 1}: not JSON`);
      return;
    }
    const at = raw.ts_rel ?? 0;
    const data = raw.data ?? {};
    switch (raw.type) {
      case 'session_start':
        rec.sessionId = String(data.session_id ?? '');
        rec.language = String(data.language ?? 'it');
        rec.run = (data.run as RunInfo | undefined) ?? null;
        rec.ui = data.ui ?? null;
        return;
      case 'turn_start': {
        const input = (data.input ?? {}) as Record<string, unknown>;
        const source = (['touch', 'text', 'voice', 'vision'].includes(String(data.source)) ? data.source : 'text') as InputSource;
        const turn: TurnSummary = {
          index: rec.turns.length,
          turnId: String(data.turn_id ?? ''),
          source,
          text: String(data.text ?? input.text ?? input.utterance ?? ''),
          stateBefore: String(data.state ?? ''),
          firstStep: rec.steps.length,
        };
        if (typeof input.button === 'string') turn.button = input.button;
        if ('data' in input) turn.data = input.data;
        rec.turns.push(turn);
        current = turn;
        rec.steps.push({ at, turnIndex: turn.index, event: { kind: 'user-said', turnId: turn.turnId, text: turn.text, source } });
        return;
      }
      case 'turn_end': {
        const turn = rec.turns.find((t) => t.turnId === data.turn_id);
        if (!turn) return;
        turn.stateAfter = str(data.state_after);
        turn.action = str(data.action);
        turn.nluPath = str(data.nlu_path);
        turn.confidence = typeof data.confidence === 'number' ? data.confidence : undefined;
        turn.totalMs = typeof data.total_ms === 'number' ? data.total_ms : undefined;
        turn.blocked = data.blocked === true;
        return;
      }
      case 'sse_assistant_message':
      case 'sse_state_update': {
        const name = raw.type === 'sse_assistant_message' ? 'assistant_message' : 'state_update';
        const parsed = parseServerEvent(name, data.payload);
        if (!parsed.ok) {
          rec.skipped.push(`line ${n + 1}: ${name}: ${parsed.reason}`);
          return;
        }
        rec.steps.push({ at, turnIndex: current?.index ?? null, event: parsed.event });
        return;
      }
      default:
        return; // model calls, guardrails…: the trace inside assistant_message already shows them
    }
  });
  return rec;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}
